/**
 * BrandLoader — the wordmark, being crossed out.
 *
 * The YAGNI fullmark is a word with a blue line through it. While a page is
 * being read, that line draws itself across the word, inking the letters as
 * it passes, then lifts off the far end and starts again. It says "working"
 * in the house's own handwriting rather than a generic spinner or a sentence.
 *
 * The mark is decorative (and outside the status region, so its letters are
 * never read); the `label` is what assistive tech hears, once, through a
 * polite `role="status"` region. It fades in after a short beat so
 * a read that lands quickly never flashes a loader at all. Under
 * `prefers-reduced-motion` the mark holds still, fully struck.
 *
 * `size="lg"` is the page-level read (centered in the pane); `size="sm"` sits
 * inline inside a section that is still reading.
 *
 * @example
 * <BrandLoader label="Reading this Team." />
 * <BrandLoader size="sm" label="Reading the checks." />
 */
import { useEffect, useState } from "react";
import { cn } from "./_utils";
import "./brand-loader.css";

export interface BrandLoaderProps {
  /** What is being read; announced to screen readers, never shown. */
  label: string;
  size?: "sm" | "lg";
  className?: string;
  "data-testid"?: string;
}

export function BrandLoader({
  label,
  size = "lg",
  className,
  "data-testid": testId,
}: BrandLoaderProps) {
  // The live region mounts empty and takes the label a tick later: some
  // screen readers announce only CHANGES to a live region, never its first
  // content, so a label present at mount could go unheard.
  const [said, setSaid] = useState("");
  useEffect(() => setSaid(label), [label]);

  return (
    <div className={cn("bl", `bl-${size}`, className)} data-testid={testId}>
      <span className="bl-mark" aria-hidden="true">
        <span className="bl-word">YAGNI</span>
        <span className="bl-word bl-ink">YAGNI</span>
        <span className="bl-strike" />
      </span>
      <span role="status" aria-live="polite" className="sr-only">
        {said}
      </span>
    </div>
  );
}
